import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import { Box, PostalCodeCheckForm, Stack } from '@ads-core/components';
import { PostalCodeCheckFormProps } from '@ads-core/types';

import { IntegrationApi } from '@alliander-fe/api';

import { TextFieldProps } from '@alliander-fe/sitecore-types';
import { RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import { FilterValuePropertyFromFields } from 'src/utils/mapWithoutValueProperty';
import {
  useTrackingCustomWizardFormFieldError,
  useTrackingCustomWizardInvalidSubmit,
} from 'src/context/Tracking/Wizard';

import { capitalizeLocationName, setDisplayPostalCode } from 'src/utils';
import { useTrackingContext, useTrackingFormTouch } from 'src/context/TrackingContext';
import { useAdobeTarget } from 'src/hooks/useAdobeTargetView';

import {
  houseNumberAdditionPattern,
  houseNumberPattern,
  postalCodePattern,
} from '@alliander-fe/validation';

import { extractString } from '../Form/utils/utils';

import DialogNetwork, { DialogAddressProps, DialogStatusProps } from './_DialogNetwork';
import { handleAddressErrorRequest } from './utils/handleAddressErrorRequest';
import { handleCongestionSuccessRequest } from './utils/handleCongestionSuccessRequest';
import { handleStaticCongestion } from './utils/handleStaticCongestion';
import { handleAddressSuccessRequest } from './utils/handleAddressSuccessRequest';
import { formatPostalCode } from './utils/formatPostalcode';
import { ABTestSubmitButton } from './_ABTestSubmitButton';

export type FormInputProps = {
  postalCode: string;
  houseNumber: string;
  houseNumberAddition?: string;
};

export type AddressDataProps = FormInputProps & {
  city?: string;
};

export type FeatureToggleProps = {
  networkCheck?: boolean;
  outageCheck?: boolean;
  congestionCheck?: boolean;
};

export type NetworkCheckFormProps = {
  networkCheck?: FilteredNetworkCheckProps;
  color?: PostalCodeCheckFormProps['color'];
  featureToggle?: FeatureToggleProps;
};

type DialogNetworkProps = {
  networkOutage?: DialogStatusProps;
  networkAvailability?: DialogStatusProps;
  networkCongestions?: DialogStatusProps;
} & Omit<DialogAddressProps, 'modalTag'>;

// With this component we can provide the user with the congestion and outage data on their address:
// To Request the data we need the users postalCode, houseNummer and houseNumberAddition.

// For Network Availability:
// We will only show a message they Liander is Not Operational on their address.

// For the Outage:
// Will only be available when the user address is within the Liander service area.
// Shows if theres a outage on a energyType or not.

// For the congestions:
// When the 'featureToggle.congestionCheck' is false only the 'congestionsTitle' and 'congestionsText' will be visible
// When the 'featureToggle.congestionCheck' is true we will always request the 'mutateCongestions'.
// For the congestions we have the "{queryParameters}" params to redirect to the congestion page.

const WIZARD_NAME = 'Netwerk checker';

export const NetworkCheckForm = ({ networkCheck, color, featureToggle }: NetworkCheckFormProps) => {
  const version = networkCheck?.useCongestionCheckAPICallVersionTwo ? 'v2' : 'v1';

  const [dialogData, setDialogData] = useState<DialogNetworkProps>();
  const { trackCustomWizard } = useTrackingContext();
  const ref = React.useRef<HTMLDivElement | null>(null);

  const { error, offer } = useAdobeTarget({
    mbox: 'network-check-mbox',
    selector: ref,
  });

  const isTestVariant = Boolean(!error && offer && offer[0]?.content[0]?.variant === 'B');

  const { register, handleSubmit, getValues, formState } = useForm({
    mode: 'onBlur',
  });

  const mutateAddress = useMutation({
    mutationFn: IntegrationApi.serviceAvailabilityEndpointsGetServiceAvailabilityDetails,
  });

  const mutateCongestions = useMutation({
    mutationFn: IntegrationApi.congestionEndpointsGetSmallScaleCongestions,
  });

  const isLoading = mutateAddress.status === 'pending' || mutateCongestions.status === 'pending';

  const formErrors = Object.values(formState.errors)
    .map((error) => error?.message)
    .filter((message): message is string => message !== undefined);

  useTrackingFormTouch({
    wizardName: WIZARD_NAME,
    wizardStep: 1,
    fields: formState.touchedFields,
  });

  useTrackingCustomWizardFormFieldError({
    fieldsErrors: formErrors,
    actionDetail: `${WIZARD_NAME} foutief ingevuld`,
    wizardName: WIZARD_NAME,
    wizardStep: 1,
  });

  const { handleInvalidSubmit } = useTrackingCustomWizardInvalidSubmit({
    fieldValues: [getValues('postalCode'), getValues('houseNumber')],
    actionDetail: `${WIZARD_NAME} niet volledig ingevuld`,
    wizardName: WIZARD_NAME,
    wizardStep: 1,
  });

  // Submit the form and sends the request when the form is valid.
  const onSubmit: SubmitHandler<FormInputProps> = async (data: FormInputProps) => {
    setDialogData(undefined);

    const { postalCode, houseNumber, houseNumberAddition } = data;

    // Get the valid forms and create the displayPostalCode for the viewport
    const displayPostalCode = setDisplayPostalCode(postalCode);
    const requestPostalCode = formatPostalCode(postalCode);
    const houseNumberAdditionUppercase = houseNumberAddition?.toUpperCase();

    let userAddress: AddressDataProps = {
      postalCode: displayPostalCode,
      houseNumber: houseNumber,
      houseNumberAddition: houseNumberAdditionUppercase,
    };

    // Gets the user address data and create a response based on the data.
    if (featureToggle?.networkCheck || featureToggle?.outageCheck) {
      mutateAddress.mutate(
        {
          postalCode: requestPostalCode,
          houseNumber: Number(houseNumber),
          addition: houseNumberAdditionUppercase ?? null,
        },
        {
          onError: (error) => {
            const { networkAvailability } = handleAddressErrorRequest({
              error,
            });

            // Handles the errors for the serviceAvailability
            setDialogData((prevProps) => {
              return {
                ...prevProps,
                networkAvailability,
                address: userAddress,
              };
            });

            trackCustomWizard({
              action: 'field validation error',
              actionDetail: error.message || networkAvailability.message,
              wizardName: WIZARD_NAME,
              wizardStep: 1,
            });
          },
          onSuccess: ({
            address,
            outageNumber,
            electricityNetwork,
            gasNetwork,
            outagesRetrievedWithPartialPostalCode,
          }) => {
            userAddress = {
              ...userAddress,
              houseNumber: houseNumber.toString(),
              city: address?.city ? capitalizeLocationName(address.city) : undefined,
            };

            const { networkAvailability, networkOutage, isOperational } =
              handleAddressSuccessRequest({
                address: userAddress,
                outageNumber,
                electricityNetwork,
                gasNetwork,
                featureToggle,
                networkCheck,
                outagesRetrievedWithPartialPostalCode,
              });

            setDialogData((prevProps) => {
              return {
                ...prevProps,
                networkAvailability,
                networkOutage,
                address: userAddress,
              };
            });

            if (!isOperational) {
              trackCustomWizard({
                action: 'proceed',
                actionDetail: 'Adres buiten Liander gebied',
                wizardName: WIZARD_NAME,
                wizardStep: 2,
              });
            } else {
              trackCustomWizard({
                action: 'proceed',
                actionDetail: `${WIZARD_NAME} succesvol`,
                wizardName: WIZARD_NAME,
                wizardStep: 2,
              });
            }
          },
        }
      );
    }

    // Get the congestion data based on the postal code.
    if (featureToggle?.congestionCheck) {
      mutateCongestions.mutate(
        {
          PostalCode: requestPostalCode,
          HouseNumber: Number(houseNumber),
          Addition: houseNumberAdditionUppercase,
          Version: version,
        },
        {
          onError: (error) => {
            console.error('onError', error.message);
          },
          onSuccess: ({ level }) => {
            const networkCongestions = handleCongestionSuccessRequest({
              address: userAddress,
              level,
              networkCheck,
              version: version,
            });

            setDialogData((prevState) => {
              return {
                ...prevState,
                networkCongestions,
                address: userAddress,
              };
            });
          },
        }
      );
    }

    // When the the FeatureToggle for the congestionCheck is off we show only some text.
    if (
      (networkCheck?.fullCongestionsTitle || networkCheck?.fullCongestionsText) &&
      !featureToggle?.congestionCheck
    ) {
      const networkCongestions = handleStaticCongestion({
        address: userAddress,
        networkCheck,
      });

      setDialogData((prevState) => {
        return {
          ...prevState,
          networkCongestions,
          address: userAddress,
        };
      });
    }
  };

  if (!networkCheck) return null;

  return (
    <Box ref={ref}>
      <PostalCodeCheckForm handleOnSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}>
        <PostalCodeCheckForm.Container color={color}>
          <PostalCodeCheckForm.Heading>
            {networkCheck.title ??
              'networkCheckControleer voor uw adres of het stroomnet vol is en of er storingen zijn'}
          </PostalCodeCheckForm.Heading>
          <PostalCodeCheckForm.Input
            label={networkCheck?.postcode?.toString() ?? 'Postcode'}
            tone="onDark"
            placeholder="1234AB"
            error={extractString(formState?.errors?.postalCode?.message)}
            {...register('postalCode', {
              required: {
                value: true,
                message: '"Postcode" is een verplicht veld',
              },
              pattern: postalCodePattern,
            })}
            autoComplete="postal-code"
          />
          <PostalCodeCheckForm.Addendum
            label={networkCheck?.huisnr?.toString() ?? 'Huisnr. en toevoeging'}
            error={extractString(formState?.errors?.houseNumber?.message)}
            tone="onDark"
            houseNumber={{
              label: 'Huisnummer',
              placeholder: '10',
              error: extractString(formState?.errors?.houseNumber?.message),
              ...register('houseNumber', {
                required: {
                  value: true,
                  message: '"Huisnummer" is een verplicht veld',
                },
                pattern: houseNumberPattern,
              }),
            }}
            addition={{
              label: 'Toevoeging',
              placeholder: 'A',
              error: extractString(formState?.errors?.houseNumberAddition?.message),
              ...register('houseNumberAddition', {
                pattern: houseNumberAdditionPattern,
              }),
            }}
          />
          <Box paddingTop={1} width="100%">
            {!formState.isValid ? (
              <ABTestSubmitButton isTestVariant={isTestVariant}>
                {networkCheck?.button ?? 'Controleren'}
              </ABTestSubmitButton>
            ) : (
              <DialogNetwork.Dialog
                trigger={
                  <PostalCodeCheckForm.SubmitButton size="large" tone="onDark" variant="secondary">
                    {networkCheck?.button ?? 'Controleren'}
                  </PostalCodeCheckForm.SubmitButton>
                }
              >
                {isLoading ? (
                  <DialogNetwork.Loading />
                ) : (
                  <>
                    <DialogNetwork.Address
                      address={dialogData?.address}
                      modalTag={networkCheck.greetingTitle}
                    />
                    <DialogNetwork.Heading>{networkCheck.feedbackModalTitle}</DialogNetwork.Heading>
                    <DialogNetwork.Description>
                      <Stack gap={4}>
                        {(dialogData?.networkAvailability &&
                          !dialogData?.networkAvailability?.status) ||
                        (dialogData?.networkAvailability?.status &&
                          !dialogData?.networkCongestions) ? (
                          <DialogNetwork.Status {...dialogData.networkAvailability} />
                        ) : null}
                        {dialogData?.networkOutage ? (
                          <DialogNetwork.Status {...dialogData.networkOutage} />
                        ) : null}
                        {dialogData?.networkCongestions ? (
                          <DialogNetwork.Status {...dialogData.networkCongestions} />
                        ) : null}
                      </Stack>
                    </DialogNetwork.Description>
                  </>
                )}
              </DialogNetwork.Dialog>
            )}
          </Box>
        </PostalCodeCheckForm.Container>
      </PostalCodeCheckForm>
    </Box>
  );
};

export type SiteCoreNetworkCheckProps = {
  title: TextFieldProps;
  postcode: TextFieldProps;
  huisnr: TextFieldProps;
  button: TextFieldProps;

  greetingTitle: TextFieldProps;
  feedbackModalTitle: TextFieldProps;

  isNotGridOperatorTitle: TextFieldProps;
  isNotGridOperatorText: RichTextField;
  isElectricityAndGasOperatorTitle: TextFieldProps;
  isElectricityAndGasOperatorText: RichTextField;
  isElectricityNotGasOperatorTitle: TextFieldProps;
  isElectricityNotGasOperatorText: RichTextField;
  isGasNotElectricityOperatorTitle: TextFieldProps;
  isGasNotElectricityOperatorText: RichTextField;

  noOutagesTitle: TextFieldProps;
  noOutagesText: RichTextField;
  gasOutageTitle: TextFieldProps;
  gasOutageText: RichTextField;
  electricityOutageTitle: TextFieldProps;
  electricityOutageText: RichTextField;
  gasAndElectricityOutageText: TextFieldProps;
  gasAndElectricityOutageTitle: RichTextField;

  noCongestionsTitle: TextFieldProps;
  noCongestionsText: RichTextField;
  fullCongestionsTitle: TextFieldProps;
  fullCongestionsText: RichTextField;
  veryFullCongestionsTitle: TextFieldProps;
  veryFullCongestionsText: RichTextField;

  gasOutageInPostalCodeAreaTitle: TextFieldProps;
  gasOutageInPostalCodeAreaText: RichTextField;
  electricityOutageInPostalCodeAreaTitle: TextFieldProps;
  electricityOutageInPostalCodeAreaText: RichTextField;
  gasAndElectricityOutageInPostalCodeAreaTitle: TextFieldProps;
  gasAndElectricityOutageInPostalCodeAreaText: RichTextField;

  useCongestionCheckAPICallVersionTwo: { value: boolean };
};

export type FilteredNetworkCheckProps = FilterValuePropertyFromFields<SiteCoreNetworkCheckProps>;
